import { useState, useMemo } from 'react';
import { fetchMobileTopChartsRpdList } from '@/pages/intelligence/services/topcharts';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { ChartType, CUSTOM_FIELDS_RPD } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import {
  handleTableDefaultSelectGames,
  filterGamesWithRank,
} from '@/pages/intelligence/utils/tableUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';
import { getGameInfoByIdPlus } from '@intelligence/services/common';
import moment from 'moment';
import { toDateTypeFilter } from '@/pages/intelligence/utils/common';

export interface FetchParamsType {
  /** 固定值 */
  entity_type: 'mobile';
  data_source: ['sensortower'];
  genre: [];
  sub_genre: [];
  company: string[];
  /** 国家筛选器参数 */
  region_type: string;
  regions: string[];
  /** 排序 */
  order_metric: string;
  order: 'desc' | 'asc';
  /** 表格分页器参数 */
  page: number;
  page_size: number;
  /** 时间筛选器参数 */
  date_type: 'daily' | 'weekly' | 'monthly';
  start_date: string;
  end_date: string;
  /** ReleaseDate筛选器参数 */
  start_release_time: string;
  end_release_time: string;
  /** 搜索名称 */
  search_by_name: string;
  datasource_sim?: string;
  /** 获取指定游戏的id(比如搜索游戏，折线图请求指定游戏) */
  unified_ids: string[];
  rank_ids?: string[];
  sensortower_tag_filter: any[];
  filter_ids: string[];
  genre_source: 'iegg';
  iegg_genre_req: [];
  group_by?: string[];
  order_by?: string[];
}

/** 这里处理页面交互逻辑
 * 1 Confirm接口请求
 * 2 lineType请求折线图
 * 3 checkbox可视化柱状图交互|或重新请求折线图
 * 4
 */
const useRpdModel = (props: any) => {
  const {
    chartType,
    filterValue,
    setFilterVale,
    hasUrlShortLink,
    defaultFilterValue,
    setTableLoading,
    setIsVisualisLoading,
  } = props;

  const [tableData, setTableData] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);

  // 柱状图数据
  const [barOrPieData, setBarOrPieData] = useState<any>([]);
  const [lineData, setLineData] = useState<any>({});
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  const defaultFetchParams: FetchParamsType = {
    entity_type: 'mobile',
    data_source: ['sensortower'],
    region_type: 'region',
    regions: ['global'],
    genre: [],
    sub_genre: [],
    date_type: 'monthly',
    company: [],
    start_release_time: '',
    end_release_time: '',
    order_metric: 'rpd',
    order: 'desc',
    search_by_name: '',
    unified_ids: [],
    page: 1,
    page_size: 50,
    start_date: '',
    end_date: '',
    sensortower_tag_filter: [],
    filter_ids: [],
    rank_ids: [],
    group_by: [],
    order_by: ['rpd desc'],
    genre_source: 'iegg',
    iegg_genre_req: [],
  };

  const [fetchParams, setFetchParams] = useState<FetchParamsType>(defaultFetchParams);
  // 是否重新勾选过游戏
  const [operatedGame, setOperatedGame] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);
  // 当前游戏列表游戏gameInfo
  const [gameInfoMapCur, setGameInfoMapCur] = useState<any>({});
  const [gameDetailsLoading, setGameDetailsLoading] = useState(true);

  const srouceValueCur = useMemo(() => {
    return Array.isArray(fetchParams?.data_source) && fetchParams?.data_source?.length > 0
      ? fetchParams?.data_source[0]
      : 'sensortower';
  }, [fetchParams]);

  // 转换数据结构-宽表
  const transRpdData = (list: any, gameInfoMap: any, params: any) => {
    return list?.map((item: any) => {
      return {
        rank: item?.rank,
        unifiedId: item?.unified_id,
        gameIcon: gameInfoMap?.[item?.unified_id]?.cover,
        gameName: gameInfoMap?.[item?.unified_id]?.entity_name,
        gameOhterName: gameInfoMap?.[item?.unified_id]?.other_name,
        gameCompany: gameInfoMap?.[item?.unified_id]?.publisher,
        gameMarketName: gameInfoMap?.[item?.unified_id]?.country_abbre,
        gameFullMarketName: gameInfoMap?.[item?.unified_id]?.country_name_en,
        isSpecialReleaseDate: gameInfoMap?.[item?.unified_id]?.is_special_release_time,
        releaseDate: gameInfoMap?.[item?.unified_id]?.release_time,
        releaseDateEN: gameInfoMap?.[item?.unified_id]?.release_time_en,
        releaseDateCN: gameInfoMap?.[item?.unified_id]?.release_time_cn,
        mainGenre:
          params.genre_source === 'iegg'
            ? gameInfoMap?.[item?.unified_id]?.iegg_genre
            : gameInfoMap?.[item?.unified_id]?.genre,
        subgenre:
          params.genre_source === 'iegg'
            ? gameInfoMap?.[item?.unified_id]?.iegg_sub_genre
            : gameInfoMap?.[item?.unified_id]?.sub_genre,
        rpd: item?.rpd,
        revenue: item?.revenue,
        download: item?.download,
        total_count: item?.total_count,
      };
    });
  };

  // 获取history
  const getHistory = (metric: string, itemList: any) => {
    return itemList?.map((item: any) => {
      return {
        time: item?.date,
        value: item?.[metric],
        source: srouceValueCur,
        metric,
      };
    });
  };

  // 转换指标time-line格式
  const getTimeLineData = (metric: string, groupedArray: any, gameInfoMapValue: any) => {
    const gameObjList: any = [];

    groupedArray?.forEach((itemList: any) => {
      if (Array.isArray(itemList) && itemList?.length > 0) {
        const gameId = itemList?.[0]?.unified_id || '';
        gameObjList?.push({
          cover: gameInfoMapValue?.[gameId]?.cover,
          history: getHistory(metric, itemList),
          id: gameId,
          name: gameInfoMapValue?.[gameId]?.entity_name,
        });
      }
    });

    return gameObjList;
  };

  // 转换数据结构-折线图
  const transRpdDataLine = (list: any, gameInfoMapValue: any) => {
    const groupedArray = list?.reduce((gameList: any, current: any) => {
      const existingGroup = gameList?.find(
        (gameGroup: any) => gameGroup[0]?.unified_id === current.unified_id,
      );
      if (existingGroup) {
        existingGroup.push(current);
      } else {
        gameList.push([current]);
      }
      return gameList;
    }, []);

    return {
      download: getTimeLineData('download', groupedArray, gameInfoMapValue),
      revenue: getTimeLineData('revenue', groupedArray, gameInfoMapValue),
      rpd: getTimeLineData('rpd', groupedArray, gameInfoMapValue),
    };
  };

  const getGameData = async (argums: {
    params: FetchParamsType;
    isCheckOrSearch: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
  }) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
    } = argums;
    // 当时间为空时，等时间返回再更新，否则直接写token到接口
    const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.start_date || !params?.end_date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }

    setTableData([]);
    const paramsValue = {
      ...params,
      data_source: [params.datasource_sim || defaultFilterValue.dataSourceSingle?.value],
      order_source: params.datasource_sim || defaultFilterValue.dataSourceSingle?.value,
      // unified_ids: params?.filter_ids || [],
      start_date:
        toDateTypeFilter[params.date_type] === 'month' && params.start_date !== ''
          ? `${moment(params.start_date).format('YYYY-MM')}-01`
          : params.start_date,
      end_date:
        toDateTypeFilter[params.date_type] === 'month' && params.end_date !== ''
          ? `${moment(params.end_date).format('YYYY-MM')}-01`
          : params.end_date,
    } as any;

    delete paramsValue.datasource_sim;

    setFetchParams(paramsValue);
    const isSearch = paramsValue?.rank_ids?.length > 0;

    setTableLoading(true);
    setIsVisualisLoading(true);

    fetchMobileTopChartsRpdList(paramsValue)
      .then(async (result) => {
        const listValue = result?.data?.list || [];
        const totalTemp = result?.data?.total || listValue?.length; // 搜索不返回total用表格长度计算
        setTableTotal(totalTemp);

        if (result?.data?.list) {
          const gameInfoParams = {
            entity_type: paramsValue.entity_type,
            ids: result?.data?.list?.map((item: any) => item.unified_id),
            custom_fields: CUSTOM_FIELDS_RPD,
            level: 'custom',
          };

          try {
            setGameDetailsLoading(true);
            const res = await getGameInfoByIdPlus(gameInfoParams);
            // translateStateResponse 公共方法，二合一转为旧数据格式
            const gameInfoMap = res?.data || {};
            setGameInfoMapCur(gameInfoMap);
            const listTemp = result?.data?.list || [];
            const total = result?.data?.total || listTemp?.length; // 搜索不返回total用表格长度计算

            // 转换为原来数据结构
            const list = transRpdData(listTemp, gameInfoMap, paramsValue);

            let checkGameList = tableSelection;

            if (paramsValue.page === 1 && !isCheckOrSearch) {
              // 过滤rank==0后的游戏可勾选展示可视化
              const filterNoRankGameList = filterGamesWithRank(list);
              /** 默认选择第一页的前6作为表格全选展示 */
              const defaultGames = handleTableDefaultSelectGames(
                filterNoRankGameList?.slice(0, TOPGAMENUM),
                'unifiedId',
              );
              setDefaultSelectionGames(defaultGames);
              /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
              if (!isCheckOrSearch) {
                setTableSelection(defaultGames);
                const barOrPieDataTmp = handleTableToBar(filterNoRankGameList);
                setBarOrPieData(barOrPieDataTmp?.slice(0, TOPGAMENUM));

                checkGameList = defaultGames;
              }
            }
            if (!isSearch) {
              // 有game_code表示是搜索，则不存页面数据
              setTableListForClearSearch(list);
              setTotalForClearSearch(total);
            }

            setTableData(list || []);

            /** 时间及新筛选器参数回填 */
            const startDate = result?.req?.start_date;
            const endDate = result?.req?.end_date;
            const newFetchParams = {
              ...paramsValue,
              start_date: startDate || '',
              end_date: endDate || '',
              filter_ids: isSearch ? paramsValue?.filter_ids : result?.req?.filter_ids,
              unified_ids: isSearch ? paramsValue?.unified_ids : result?.req?.unified_ids,
            };
            setFetchParams(newFetchParams);

            // 获取到时间后更新筛选器的值，并更新url短链接
            if (needUpdateTime && startDate && endDate) {
              const newFilterValue = updateTimeFilterValue({
                // hasUrlShortLinkVal,
                haveFilterSaveBtn: true,
                filterSaveName,
                filterValue: updateFilterValues,
                timeFilterValues: {
                  type: newFetchParams.date_type,
                  value: [newFetchParams.start_date, newFetchParams.end_date],
                },
              });
              setFilterVale(newFilterValue);
              // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
              if (!isUpdateShortLinkNow && hasUrlShortLink) {
                updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
              }
            }

            if (chartType === ChartType.Line && isUpdateLineChart) {
              if (checkGameList.length > 0) {
                const idList = checkGameList.map((t) => t?.gameId);
                getLineChartData({ ...paramsValue, unified_ids: idList || [] }, gameInfoMap);
              } else {
                setLineData([]);
              }
            }

            // return result;
          } catch (error) {
            // reject(error);
          } finally {
            setGameDetailsLoading(false);
            setTableLoading(false);
            setIsVisualisLoading(false);
          }
        } else {
          setGameDetailsLoading(false);
          setTableLoading(false);
          setIsVisualisLoading(false);
        }
      })
      .catch(() => {
        // setLineChartData([]);
        // loadingDispatch({ type: 'lineChart', payload: false });
        setTableLoading(false);
        setIsVisualisLoading(false);
      });
  };

  const getLineChartData = async (
    params: FetchParamsType,
    gameInfoMapValue: any = gameInfoMapCur,
  ) => {
    try {
      if (!params?.unified_ids?.length) {
        throw new Error();
      }
      setLineData({});
      const newFetchParams = {
        ...params,
        group_by: ['date'],
        page_size: 100000,
      };
      const res = await fetchMobileTopChartsRpdList(newFetchParams);
      // console.log('resres', res);
      // const lineDataRes = res?.data || {};
      const lineListTemp = res?.data?.list || [];
      const lineList = [...lineListTemp]?.sort((a: any, b: any) => {
        if (b.date !== a.date) return moment(a?.date).diff(moment(b?.date));
      });
      // 转换数据结构-折线图
      const lineDataRes = transRpdDataLine(lineList, gameInfoMapValue) || {};

      const temp = {
        rpd: Array.isArray(lineDataRes.rpd) ? lineDataRes.rpd : [],
        revenue: Array.isArray(lineDataRes.revenue) ? lineDataRes.revenue : [],
        download: Array.isArray(lineDataRes.download) ? lineDataRes.download : [],
      };
      // console.log('temp', temp);
      setLineData(temp);
    } catch (e) {
      console.error(e);
    } finally {
    }
  };

  const handleTableToBar = (list: any[], end = TOPGAMENUM) => {
    if (!Array.isArray(list)) {
      return [];
    }
    // TODO 将表格数据转化为柱状图数据
    return list.slice(0, end);
  };

  const isNodata = useMemo(() => {
    if (tableTotal < 1 || tableSelection.length < 1) {
      return true;
    }
    return false;
  }, [tableTotal, tableSelection]);

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, unified_ids: [], rank_ids: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = async (result: any) => {
    const resultList = result?.data?.list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const gameInfoParams = {
      entity_type: result?.req?.entity_type,
      ids: resultList?.map((item: any) => item.unified_id),
      custom_fields: CUSTOM_FIELDS_RPD,
      level: 'custom',
    };

    try {
      const res = await getGameInfoByIdPlus(gameInfoParams);
      // translateStateResponse 公共方法，二合一转为旧数据格式
      const gameInfoMap = res?.data || {};
      // setGameInfoMapCur(gameInfoMap);
      // 转换为原来数据结构
      const list = transRpdData(resultList, gameInfoMap, result?.req);
      console.log('listlist', list);
      const tranksList: RecordItemType[] = list.map((item: any) => ({
        id: item?.unifiedId,
        imgSrc: item?.gameIcon,
        lineOneStr: item?.gameName,
        lineTwoImg: getCountryFlag(item?.gameMarketName),
        lineTwoImgName: item?.gameMarketName,
        lineTwoStr: item?.gameCompany,
        rank: item?.rank,
      }));
      return tranksList;
    } catch (error) {
      return [];
    } finally {
    }
  };

  return {
    // tableLoading,
    // chartLoading,
    tableTotal,
    tableData,
    barOrPieData,
    setBarOrPieData,
    lineData,
    fetchParams,
    defaultFetchParams,
    getGameData,
    tableSelection,
    defaultSelectionGames,
    getLineChartData,
    operatedGame,
    setOperatedGame,
    isNodata,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
    srouceValueCur,
    gameDetailsLoading,
    gameDetailsMapping: gameInfoMapCur,
  };
};

export default useRpdModel;
